import {
	Button,
	Checkbox,
	Input,
	Label,
	Modal,
	ModalContent,
	ModalDescription,
	ModalFooter,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { ModalHeader } from "~/core/ui/dialog";
import { Async, Empty } from "~/core/ui/states";
import { Markdown } from "~/modules/analytics/components/markdown";
import { useAgentInbox } from "~/modules/analytics/lib/agent-inbox";
import {
	AGENT_INBOX_CHANGED,
	agents,
	type FieldSchema,
	type FormSchema,
	type InboxItem,
} from "~/modules/analytics/lib/agents";

/** What each control holds: a checkbox's state, or the text typed or the option index chosen. */
export type Draft = Record<string, string | boolean>;

/** A yes/no decision, answered by the two buttons rather than by a field. */
function isApproval(schema: FormSchema): boolean {
	return schema.properties?.approved?.type === "boolean";
}

export function initialDraft(schema: FormSchema): Draft {
	return Object.fromEntries(Object.entries(schema.properties ?? {}).map(([key, field]) => [key, draftOf(field)]));
}

function draftOf(field: FieldSchema): string | boolean {
	const value = field.default;
	if (field.type === "boolean") return value === true;
	if (value === undefined || value === null) return "";
	if (field.enum) {
		const index = field.enum.findIndex((item) => JSON.stringify(item) === JSON.stringify(value));
		return index < 0 ? "" : String(index);
	}
	return typeof value === "string" ? value : JSON.stringify(value);
}

/**
 * The input the workflow resumes with, typed as its schema declares, and the
 * fields that stop it being sent: a required one left empty, or text that is
 * not the number or JSON the field asks for.
 */
export function answer(schema: FormSchema, draft: Draft): { input: Record<string, unknown>; invalid: string[] } {
	const input: Record<string, unknown> = {};
	const invalid: string[] = [];
	for (const [key, field] of Object.entries(schema.properties ?? {})) {
		const parsed = parse(field, draft[key] ?? "");
		if (!parsed.ok || (parsed.value === undefined && schema.required?.includes(key))) invalid.push(key);
		else if (parsed.value !== undefined) input[key] = parsed.value;
	}
	return { input, invalid };
}

function parse(field: FieldSchema, raw: string | boolean): { ok: boolean; value?: unknown } {
	if (field.type === "boolean") return { ok: true, value: raw === true };
	const text = String(raw);
	if (text.trim() === "") return { ok: true };
	if (field.enum) return { ok: true, value: field.enum[Number(text)] };
	if (field.type === "string") return { ok: true, value: text };
	if (field.type === "number" || field.type === "integer") {
		const n = Number(text);
		return Number.isFinite(n) && (field.type === "number" || Number.isInteger(n))
			? { ok: true, value: n }
			: { ok: false };
	}
	try {
		return { ok: true, value: JSON.parse(text) };
	} catch {
		return { ok: false };
	}
}

export function AgentInbox({ org }: { org: string }) {
	const t = useT();
	const { locale } = useI18n();
	const state = useAgentInbox(org);
	const [open, setOpen] = useState<InboxItem | null>(null);
	return (
		/* No heading of its own: it is a tab now, and the tab is the heading —
		   with the count on it, where the reader is choosing between the three. */
		<section className="section" id="agent-inbox" aria-label={t("tasks.inbox")}>
			<Async state={state}>
				{({ items }) =>
					items.length ? (
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead>{t("tasks.name")}</TableHead>
									<TableHead>{t("runs.brand")}</TableHead>
									<TableHead>{t("tasks.type")}</TableHead>
									<TableHead>{t("tasks.deadline")}</TableHead>
									<TableHead>
										<span className="sr-only">{t("tasks.review")}</span>
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{items.map((item) => (
									<TableRow key={item.context_id}>
										<TableCell className="wrap">
											{item.workflow_name}
											{item.title ? <span className="muted"> · {item.title}</span> : null}
										</TableCell>
										<TableCell>{item.brand}</TableCell>
										<TableCell>{t(item.approval ? "tasks.approval" : "tasks.inputNeeded")}</TableCell>
										<TableCell>{fmt.dateTime(locale, item.deadline)}</TableCell>
										<TableCell>
											<div className="record-actions">
												<Button variant="default" size="sm" onClick={() => setOpen(item)}>
													{t("tasks.review")}
												</Button>
												<Button asChild variant="link" size="sm">
													<Link to={`/w/${item.entity_id}/agent/w/${item.workflow_id}`}>{t("tasks.openWorkflow")}</Link>
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<Empty title={t("tasks.inboxEmptyTitle")} body={t("tasks.inboxEmpty")} />
					)
				}
			</Async>
			{open ? <RespondModal item={open} onClose={() => setOpen(null)} /> : null}
		</section>
	);
}

function RespondModal({ item, onClose }: { item: InboxItem; onClose: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { operable, why } = useAccess();
	const [draft, setDraft] = useState(() => initialDraft(item.schema));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const approval = isApproval(item.schema);
	const fields = Object.entries(item.schema.properties ?? {}).filter(([key]) => !(approval && key === "approved"));
	const blocked = answer(item.schema, draft).invalid.length > 0;

	async function submit(decision: Draft = {}) {
		const { input, invalid } = answer(item.schema, { ...draft, ...decision });
		if (invalid.length) return;
		setBusy(true);
		setError(null);
		try {
			await agents.respond(item, input);
			toast.success(t("tasks.responded"));
			onClose();
			// The inbox and the sidebar badge both reload on this.
			window.dispatchEvent(new Event(AGENT_INBOX_CHANGED));
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
			// Somebody else answered it, or it timed out: the row is stale.
			if (e instanceof ApiError && e.status === 409) window.dispatchEvent(new Event(AGENT_INBOX_CHANGED));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Modal open onOpenChange={(next) => !next && onClose()}>
			<ModalContent>
				<ModalHeader>{item.title ?? item.workflow_name}</ModalHeader>
				<ModalDescription>
					{item.title ? `${item.workflow_name} · ` : ""}
					{item.brand} · {t("tasks.deadline")} {fmt.dateTime(locale, item.deadline)}
				</ModalDescription>
				{item.description ? <Markdown text={item.description} /> : null}
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (!approval) void submit();
					}}
				>
					<fieldset disabled={busy || !operable} className="stack bare-fieldset">
						{fields.map(([key, field]) => (
							<Field
								key={key}
								id={`respond-${item.context_id}-${key}`}
								name={key}
								field={field}
								required={item.schema.required?.includes(key) ?? false}
								value={draft[key] ?? ""}
								onChange={(value) => setDraft((previous) => ({ ...previous, [key]: value }))}
							/>
						))}
						<p className="hint">{t(approval ? "tasks.approvalHint" : "tasks.inputHint")}</p>
						{error ? (
							<p role="alert" className="error">
								{error}
							</p>
						) : null}
						<ModalFooter>
							{approval ? (
								<>
									<Button
										type="button"
										variant="default"
										disabled={blocked}
										title={whyDisabled(operable, why.editor)}
										onClick={() => void submit({ approved: false })}
									>
										{t("tasks.reject")}
									</Button>
									<Button
										type="button"
										disabled={blocked}
										title={whyDisabled(operable, why.editor)}
										onClick={() => void submit({ approved: true })}
									>
										{t("tasks.approve")}
									</Button>
								</>
							) : (
								<Button type="submit" disabled={blocked} title={whyDisabled(operable, why.editor)}>
									{t("tasks.submit")}
								</Button>
							)}
						</ModalFooter>
					</fieldset>
				</form>
			</ModalContent>
		</Modal>
	);
}

function Field({
	id,
	name,
	field,
	required,
	value,
	onChange,
}: {
	id: string;
	name: string;
	field: FieldSchema;
	required: boolean;
	value: string | boolean;
	onChange: (value: string | boolean) => void;
}) {
	const t = useT();
	const label = (
		<Label htmlFor={id}>
			{field.title || name}
			{required ? " *" : ""}
		</Label>
	);
	const hint = field.description ? <p className="hint">{field.description}</p> : null;
	if (field.type === "boolean")
		return (
			<div className="stack">
				<div className="flex items-center gap-(--s-4)">
					<Checkbox id={id} checked={value === true} onCheckedChange={(checked) => onChange(checked === true)} />
					{label}
				</div>
				{hint}
			</div>
		);
	const text = String(value);
	return (
		<div className="stack">
			{label}
			{field.enum ? (
				<NativeSelect id={id} aria-required={required} value={text} onChange={(e) => onChange(e.target.value)}>
					<option value="">{t("tasks.chooseValue")}</option>
					{field.enum.map((option, index) => (
						<option key={JSON.stringify(option)} value={String(index)}>
							{typeof option === "string" ? option : JSON.stringify(option)}
						</option>
					))}
				</NativeSelect>
			) : field.type === "string" ? (
				<Input id={id} aria-required={required} value={text} onChange={(e) => onChange(e.target.value)} />
			) : field.type === "number" || field.type === "integer" ? (
				<Input
					id={id}
					type="number"
					step={field.type === "integer" ? 1 : "any"}
					aria-required={required}
					value={text}
					onChange={(e) => onChange(e.target.value)}
				/>
			) : (
				<>
					<Textarea
						id={id}
						className="mono"
						aria-required={required}
						value={text}
						onChange={(e) => onChange(e.target.value)}
					/>
					<p className="hint">{t("tasks.jsonHint")}</p>
				</>
			)}
			{hint}
		</div>
	);
}
